import { MapPinIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Button, Card, InlineBanner, LinkButton, notify, PageHeader, Skeleton, SkeletonRegion, StateBlock, Switch, type UrlTab, UrlTabs } from '../../components/ui';
import { OWNER_ABOVE } from '../../constants/roles';
import { useApiError } from '../../hooks/useApiError';
import useAuth from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import api, { getApiError } from '../../services/api';
import type { LocationRecord } from './apiTypes';
import { ActiveMenuCard } from './components/ActiveMenuCard';
import { LocationCodeCard } from './components/LocationCodeCard';
import { LocationStats } from './components/LocationStats';
import { TableCodesPanel } from './components/TableCodesPanel';
import { useRequest } from './useRequest';

const LocationDetail = () => {
    const { id = '' } = useParams<{ id: string }>();
    const { t } = useTranslation(['locations', 'common']);
    const { hasRole } = useAuth();
    const canEdit = hasRole(OWNER_ABOVE);
    const apiError = useApiError();
    const { data: location, setData: setLocation, state, missing, retry, reload } = useRequest(`detail:${id}`, () =>
        api.get<LocationRecord>(`/locations/${id}`).then((r) => r.data)
    );
    const [switching, setSwitching] = useState(false);
    usePageTitle(location?.name ?? t('list.title'));

    if (state === 'error') {
        return (
            <>
                <PageHeader title={t('list.title')} back={{ to: '/locations', label: t('list.title') }} />
                <StateBlock
                    variant={missing ? 'empty' : 'error'}
                    icon={MapPinIcon}
                    title={missing ? t('detail.notFound') : t('detail.loadFailed')}
                    body={missing ? t('detail.notFoundBody') : t('common:ui.checkConnection')}
                    action={
                        missing ? (
                            <LinkButton to="/locations" variant="outline">
                                {t('list.title')}
                            </LinkButton>
                        ) : (
                            <Button onClick={retry}>{t('common:ui.retry')}</Button>
                        )
                    }
                />
            </>
        );
    }
    if (!location) {
        return (
            <SkeletonRegion className="flex flex-col gap-4">
                <Skeleton className="h-8 w-1/3" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-40" />
            </SkeletonRegion>
        );
    }

    const setAccepting = async (acceptingOrders: boolean) => {
        setSwitching(true);
        const before = location;
        setLocation({ ...location, acceptingOrders });
        try {
            await api.put(`/locations/${location.id}/accepting-orders`, { acceptingOrders });
            notify.success(t(acceptingOrders ? 'detail.resumed' : 'detail.paused'));
        } catch (e) {
            setLocation(before);
            notify.error(getApiError(e).status === 403 ? t('detail.switchForbidden') : apiError(e));
        } finally {
            setSwitching(false);
        }
    };

    const meta = [location.restaurant?.name, location.address, location.phoneNumber].filter(Boolean).join(' · ');
    const tabs: UrlTab[] = [
        {
            id: 'overview',
            label: t('tabs.overview'),
            panel: (
                <div className="flex flex-col gap-6">
                    <LocationStats locationId={location.id} restaurant={location.restaurant} />
                    <ActiveMenuCard location={location} canChange={canEdit} onChanged={reload} />
                </div>
            ),
        },
        { id: 'qr', label: t('tabs.qr'), panel: <QrTab location={location} /> },
        {
            id: 'orders',
            label: t('tabs.orders'),
            panel: (
                <section aria-labelledby="orders-heading">
                    <Card padding="lg">
                    <h2 id="orders-heading" className="text-title text-strong">
                        {t('orders.title')}
                    </h2>
                    <p className="mt-1 text-small text-muted">{t('orders.body')}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <LinkButton to={`/orders?view=history&locationId=${location.id}`} variant="primary">
                            {t('orders.history')}
                        </LinkButton>
                        <LinkButton to="/orders" variant="outline">
                            {t('orders.board')}
                        </LinkButton>
                    </div>
                    </Card>
                </section>
            ),
        },
    ];

    return (
        <>
            <PageHeader
                title={location.name}
                meta={meta}
                back={{ to: '/locations', label: t('list.title') }}
                actions={
                    <>
                        <Switch
                            checked={location.acceptingOrders}
                            onChange={setAccepting}
                            disabled={switching}
                            label={t('detail.accepting')}
                            description={location.acceptingOrders ? t('detail.acceptingOn') : t('detail.acceptingOff')}
                        />
                        {canEdit && (
                            <LinkButton to={`/locations/edit/${location.id}`} variant="outline" icon={<PencilSquareIcon className="size-5" aria-hidden="true" />}>
                                {t('common:ui.edit')}
                            </LinkButton>
                        )}
                    </>
                }
            />
            {!location.acceptingOrders && (
                <InlineBanner tone="neutral-strong" className="mb-6">
                    {t('detail.pausedBanner')}
                </InlineBanner>
            )}
            <UrlTabs tabs={tabs} label={t('tabs.label')} />
        </>
    );
};

const QrTab = ({ location }: { location: LocationRecord }) => (
    <div className="flex flex-col gap-6">
        <LocationCodeCard location={location} />
        <TableCodesPanel location={location} />
    </div>
);

export default LocationDetail;
